import { useTranslations } from 'next-intl';

import { ClickablePhoneNumber } from '@/components/ClickablePhoneNumber';
import { HorizontalDivider } from '@/components/HorizontalDivider';
import { Link } from '@/components/Link';
import { useUserLocalization } from '@/hooks/useUserLocalization';
import { LocationPhases } from '@/lib/locations/dto';
import { getPhoneApplicationForm } from '@/models/location-utils';
import { MEMBER_PORTAL_LOGIN_URL } from '@/utils/constants';

import SecondaryHeading from './SecondaryHeading';

type SidebarProps = {
  location?: {
    phone: string | null;
    phase: LocationPhases;
    country: string;
  };
};

export default function LocationSidebar({ location }: SidebarProps) {
  const t = useTranslations('location-sidebar');
  const { country } = useUserLocalization();
  const phone = getPhoneApplicationForm({
    location,
    country,
  });
  return (
    <div className="flex flex-col gap-4 px-5 text-sm sm:gap-6 md:gap-8 sm:px-10 xl:pr-0">
      <section>
        <SecondaryHeading>{t('call.title')}</SecondaryHeading>
        <p data-testid="prefer-to-call-description">
          {t.rich('call.description', {
            important: (chunks) => (
              <ClickablePhoneNumber phoneNumber={chunks} />
            ),
            phone,
          })}
        </p>
      </section>
      <HorizontalDivider />
      <section>
        <SecondaryHeading>{t('member.title')}</SecondaryHeading>
        <p>
          {t.rich('member.description', {
            link: (chunks) => (
              <Link
                className="underline text-ocean-60"
                href={MEMBER_PORTAL_LOGIN_URL}
                rel="noopener"
                target="_blank"
                data-testid="link-sign-in"
              >
                {chunks}
              </Link>
            ),
          })}
        </p>
      </section>
      <HorizontalDivider />
      <section>
        <SecondaryHeading>{t('checkout.title')}</SecondaryHeading>
        <p data-testid="checkout-desc">
          {t.rich('checkout.description', {
            link: (chunks) => (
              <Link
                className="text-sm underline text-ocean-60"
                href="/checkout"
                data-testid="checkout-link"
              >
                {chunks}
              </Link>
            ),
          })}
        </p>
      </section>
    </div>
  );
}
